/**
 * Self-employee name reconciliation helpers for filter values.
 *
 * Saved filter sets, session restore and user grid preferences snapshot a
 * `{ id, value, name }` shape for employee-typeahead chips (e.g. Recipients,
 * Created By). After an HR rename the snapshot still carries the old `name`
 * and the chip strip / form would keep showing the stale name until the user
 * manually re-selects themselves.
 *
 * These helpers replace the persisted `name` for any entry whose `id` matches
 * the current logged-in user. Centralised so that Tasks Search, the Link Task
 * modal and the Tasks chip strip all stay in lock step. See ADO 506080 (Bug 2).
 *
 * Equality note: persisted/JSON-restored ids commonly come back as strings
 * while `userInfo.employeeId` is a number, so we intentionally use loose
 * equality with an explicit `null/undefined` guard.
 */

export interface SelfEmployeeIdentity {
  employeeId: number | string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
}

const buildLiveName = (firstName: string | undefined, lastName: string | undefined): string =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim();

/**
 * Re-resolve the display `name` for any `{ id, value, name }` entry whose
 * `id` matches the current employee. Returns the **same array reference**
 * when nothing changed, so React deps / memos stay stable.
 *
 * `entries` is typed as `unknown` because callers receive either a raw array
 * from `react-hook-form` state or arbitrary persisted JSON; the helper
 * narrows internally and is a no-op for non-arrays.
 */
export const reconcileSelfEmployeeNameEntries = (
  entries: unknown,
  identity: SelfEmployeeIdentity,
): unknown => {
  if (!Array.isArray(entries) || identity.employeeId == null) return entries;
  const liveName = buildLiveName(identity.firstName, identity.lastName);
  if (!liveName) return entries;

  let changed = false;
  const next = entries.map(entry => {
    const id = (entry as { id?: unknown } | null)?.id;
    if (
      entry &&
      typeof entry === 'object' &&
      id != null &&
      // eslint-disable-next-line eqeqeq
      id == identity.employeeId &&
      (entry as { name?: unknown }).name !== liveName
    ) {
      changed = true;
      return { ...(entry as Record<string, unknown>), name: liveName };
    }
    return entry;
  });
  return changed ? next : entries;
};

/**
 * Apply {@link reconcileSelfEmployeeNameEntries} across the given keys of a
 * filter-values record. Returns the same record reference when no key
 * changes (cheap to drop into a React effect dep).
 */
export const reconcileSelfEmployeeNameInValues = (
  values: Record<string, unknown> | undefined,
  keys: ReadonlyArray<string>,
  identity: SelfEmployeeIdentity,
): Record<string, unknown> | undefined => {
  if (!values || identity.employeeId == null) return values;
  const liveName = buildLiveName(identity.firstName, identity.lastName);
  if (!liveName) return values;

  let out = values;
  for (const key of keys) {
    const before = out[key];
    const after = reconcileSelfEmployeeNameEntries(before, identity);
    if (after !== before) {
      if (out === values) out = { ...values };
      out[key] = after;
    }
  }
  return out;
};
